import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { OnChangeParams, Option } from 'baseui/select';
import { ApiError, PaginationMeta } from 'common/repositories/common.model';
import { camelizeKeys } from 'humps';
import useTranslation from 'next-translate/useTranslation';
import * as React from 'react';
import { useDebouncedCallback } from 'use-debounce';

import useGetLabelSelect from './use-get-label-select';
import { getDataFromQueryCache } from './use-select-input-helper';

const SELECT_DEFAULT_STALE_TIME = 60 * 1000;

interface Options<ListQueryData, ListQueryVariables> {
  useListQueryHook: (
    baseInputs: ListQueryVariables,
    baseOptions?: UseQueryOptions<ListQueryData, ApiError>,
  ) => UseQueryResult<ListQueryData, ApiError>;
  listTransformer: (result: ListQueryData) => Option[];
  paginationTransformer: (result: ListQueryData) => PaginationMeta;
  getMemoizedListVariables: (
    page: number,
    query?: string,
  ) => ListQueryVariables;
  onSelectItem: (value: Option) => void;
  resetPageWhenQueryChanged?: boolean;
  renderCreate?: boolean;
  onClickCreate?: () => void;
  createText?: string;
  value?: Option[];
  listKeyFunction?: Function;
  staleTime?: number;
  enabled?: boolean;
}

export default function useMultiSelectInputHelper<
  ListQueryData,
  ListQueryVariables,
>(helperOptions: Options<ListQueryData, ListQueryVariables>) {
  const { t } = useTranslation();
  const {
    renderCreate = false,
    createText = t('common:create_new'),
    onClickCreate = () => {},
    onSelectItem,
    useListQueryHook,
    listTransformer,
    paginationTransformer,
    getMemoizedListVariables,
    value,
    resetPageWhenQueryChanged,
    listKeyFunction,
    enabled = true,
    staleTime = SELECT_DEFAULT_STALE_TIME,
  } = helperOptions;

  const selectInputRef = React.useRef<any>();
  const [options, setOptions] = React.useState<Option[]>([]);
  const [query, setQuery] = React.useState<string | undefined>();
  const [page, setPage] = React.useState(1);

  const listVariables = React.useMemo(
    () => getMemoizedListVariables(page, query),
    [getMemoizedListVariables, page, query],
  );

  const listKey = React.useMemo(
    () => listKeyFunction?.(listVariables),
    [listKeyFunction, listVariables],
  );

  /* Is data in cache or not? */
  // Causes infinite rerender if the transformer functions are not memoized. Don't know why. Might be because of the setOptions/setSelectedOption being called every render.
  const isInListCache = React.useMemo(() => {
    if (!listKey) return false;

    const cacheNetwork = getDataFromQueryCache<ListQueryData>(
      listKey,
      staleTime,
    );

    if (!cacheNetwork) return false;

    const list = listTransformer(
      camelizeKeys(cacheNetwork as any) as unknown as ListQueryData,
    );
    if (page === 1) {
      setOptions(list);
    } else {
      setOptions((prev) => prev.concat(list));
    }
    return true;
  }, [listKey, staleTime, listTransformer, page]);

  const {
    isLoading: listLoading,
    data: list,
    error,
    refetch,
  } = useListQueryHook(listVariables, {
    enabled: enabled && !isInListCache,
    queryKey: listKey,
    staleTime: listKeyFunction ? staleTime : undefined,
    cacheTime: listKeyFunction ? staleTime * 2 : undefined,
    onSuccess: (result) => {
      if (page === 1) {
        setOptions(listTransformer(camelizeKeys(result) as any));
      } else {
        setOptions((prevOptions) =>
          prevOptions.concat(listTransformer(camelizeKeys(result) as any)),
        );
      }
    },
  });

  const loading = listLoading;

  const onMenuOpen = React.useCallback(() => {
    setQuery(undefined);
    if (error) {
      refetch();
    }
  }, [error, refetch]);

  const items = React.useMemo(() => {
    const currentOption = [
      ...options.filter((o) => {
        const found = (value || [])?.find((cur) => cur?.id === o?.id);

        return !found;
      }),
    ];

    const _options = [
      ...(value?.length ? value.concat(currentOption) : options),
    ];

    const pagination = list && paginationTransformer(list);

    if (
      pagination &&
      pagination.currentPage < pagination.lastPage &&
      (currentOption?.length || 0) + (value?.length || 0) !== pagination.total
    ) {
      _options.push({
        label: 'Load More...',
        id: 'actionLoad',
        disabled: true,
      });
    }
    if (renderCreate) {
      _options.push({ label: createText, id: 'actionCreate', disabled: true });
    }

    return _options;
  }, [createText, list, options, paginationTransformer, renderCreate, value]);

  const onQueryChange = useDebouncedCallback(
    (event) => {
      resetPageWhenQueryChanged && setPage(1);
      setQuery(event?.target?.value || '');
    },
    800,
    {
      trailing: true,
    },
  );

  const onFocus = React.useCallback(() => {
    selectInputRef.current?.setDropdownOpen?.(true);
  }, []);

  const onBlur = React.useCallback(() => {
    selectInputRef.current?.setDropdownOpen?.(false);
  }, []);

  const _onChange = React.useCallback(
    (params: OnChangeParams) => {
      onSelectItem(params.value);
    },
    [onSelectItem],
  );

  const _value = React.useMemo(() => {
    if (value) {
      return ((value as any) || []).map((e) => ({
        ...e,
        id: e.id,
        label: e.label,
      }));
    } else {
      return undefined;
    }
  }, [value]);

  const getLabel = useGetLabelSelect({
    onClickCreate,
    setIsMenuOpen: selectInputRef.current?.setDropdownOpen,
    pagination: list && paginationTransformer(list),
    setPage,
  });

  return {
    value: _value,
    options: items,
    isLoading: loading,
    onOpen: onMenuOpen,
    onInputChange: onQueryChange,
    onFocus,
    onBlur,
    getOptionLabel: getLabel as any,
    getValueLabel: getLabel,
    onChange: _onChange,
    closeOnSelect: false,
    clearable: true,
    controlRef: selectInputRef,
    maxDropdownHeight: '300px',
    overrides: {
      Popover: {
        props: {
          ignoreBoundary: true,
        },
      },
    },
  };
}
